/**
 * Cognito（Amplify）の認証エラーの分類
 *
 * パスワード誤り・セッション切れ・試行回数超過は、いずれも NotAuthorizedException で返り、
 * 区別できるのはメッセージだけである。照合規則はここに 1 か所でまとめ、採取した実際の応答
 * （`test/fixtures/cognito-auth-responses.json`）を入力にしたテストで固定する。
 *
 * 画面の文言は部品側で分類ごとに決める。例外の生メッセージは画面に出さない。
 */

/** 分類 */
export type AuthErrorKind =
  | 'invalidCredentials'
  | 'codeMismatch'
  | 'sessionExpired'
  | 'tooManyAttempts'
  | 'compromisedCredentials'
  | 'blockedForRisk'
  | 'signUpNotConfirmed'
  | 'unknown';

/** サインイン手順のエラー（想定外のステップを含む） */
export type SignInFlowError =
  | { kind: Exclude<AuthErrorKind, 'unknown'> }
  | { kind: 'unsupported'; step: string }
  | { kind: 'unknown' };

/** 漏洩パスワード（サインアップ・パスワード変更）と、リスクによる阻止に共通する文言 */
const SECURITY_REASONS = /security reasons/i;

function classifyNotAuthorized(message: string): AuthErrorKind {
  if (/invalid session/i.test(message)) {
    return 'sessionExpired';
  }
  if (/attempts exceeded|too many invalid credentials attempts|temporarily locked/i.test(message)) {
    return 'tooManyAttempts';
  }
  // 採取できていない（検証環境では再現しなかった）。Cognito のドキュメント上の文言
  // 「Unable to login because of security reasons.」に合わせる。外れても資格情報の誤りに倒れる
  if (SECURITY_REASONS.test(message)) {
    return 'blockedForRisk';
  }
  // 判別できないものは資格情報の誤りに倒す。その文言には常にパスワード再設定への導線があるため、
  // 判別し損ねた阻止でも利用者は再設定にたどり着ける
  return 'invalidCredentials';
}

export function classifyAuthError(error: unknown): { kind: AuthErrorKind } {
  if (!(error instanceof Error)) {
    return { kind: 'unknown' };
  }
  const { name, message } = error;

  switch (name) {
    case 'NotAuthorizedException':
      return { kind: classifyNotAuthorized(message) };
    // アカウントの存在を推測させないよう、誤ったパスワードと区別しない（要件 1.7）
    case 'UserNotFoundException':
      return { kind: 'invalidCredentials' };
    // サインイン時は CodeMismatchException、登録（VerifySoftwareToken）時は EnableSoftwareTokenMFAException
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return { kind: 'codeMismatch' };
    case 'TooManyRequestsException':
    case 'LimitExceededException':
    case 'TooManyFailedAttemptsException':
      return { kind: 'tooManyAttempts' };
    // 漏洩パスワードでのサインイン（Amplify の signIn は RESET_PASSWORD ステップに変換して返すが、
    // 他の API から例外のまま届いた場合も同じに扱う）
    // サインアップ後に確認コードを入力していないアカウント（Amplify の signIn は CONFIRM_SIGN_UP
    // ステップに変換して返すが、例外のまま届いた場合も同じに扱う）
    case 'UserNotConfirmedException':
      return { kind: 'signUpNotConfirmed' };
    case 'PasswordResetRequiredException':
      return { kind: 'compromisedCredentials' };
    // サインアップ・パスワード変更での漏洩パスワード。ポリシー違反と例外名が同じため文言で判別する
    case 'InvalidPasswordException':
      return { kind: SECURITY_REASONS.test(message) ? 'compromisedCredentials' : 'unknown' };
    default:
      return { kind: 'unknown' };
  }
}
